import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState("login");
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    address: "",
    agency_name: "",
    role: "operations"
  });
  const [detectedAgency, setDetectedAgency] = useState(null);
  const [detectedAgencyForReg, setDetectedAgencyForReg] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [agencyRegData, setAgencyRegData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    password: ""
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // "I have a code" tab states
  const [haveCodeData, setHaveCodeData] = useState({
    email: "",
    code: ""
  });
  const [logoUrl, setLogoUrl] = useState("https://customer-assets.emergentagent.com/job_rolebased-invoice/artifacts/q1mu9arn_chat_edit_image_20251029_170030.png");
  
  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: code & new password
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  
  // Agency Confirmation Popup states
  const [showAgencyConfirmation, setShowAgencyConfirmation] = useState(false);
  const [pendingAgencyEmail, setPendingAgencyEmail] = useState("");
  const [showVerificationCodePopup, setShowVerificationCodePopup] = useState(false);
  const [agencyVerificationCode, setAgencyVerificationCode] = useState("");
  const [showPersonalDetailsPopup, setShowPersonalDetailsPopup] = useState(false);
  const [quickRegisterData, setQuickRegisterData] = useState({
    full_name: "",
    phone: "",
    password: ""
  });

  // Handle tab changes and reset verification states
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Reset verification states when switching tabs
    if (newTab !== "agency") {
      setCodeSent(false);
      setCodeVerified(false);
      setVerificationCode("");
    }
  };

  useEffect(() => {
    fetchLogo();
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies/public/list`);
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies", error);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/company-settings/public`);
      if (response.data.logo_url) {
        setLogoUrl(`${BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error("Failed to fetch logo");
    }
  };

  const handleEmailChange = (email) => {
    // Auto-detect agency by email domain
    let agency = null;
    if (email.includes('@')) {
      const domain = email.split('@')[1].toLowerCase();
      const matchingAgency = agencies.find(a => {
        const agencyDomain = a.email.split('@')[1]?.toLowerCase();
        return agencyDomain === domain;
      });
      agency = matchingAgency || null;
    }
    
    setDetectedAgency(agency);
    setRegisterData({ 
      ...registerData, 
      email,
      agency_name: agency ? agency.name : ""
    });
  };

  const handleAgencyEmailChange = (email) => {
    // Auto-detect agency by email domain for agency registration
    let agency = null;
    if (email.includes('@')) {
      const domain = email.split('@')[1].toLowerCase();
      
      const matchingAgency = agencies.find(a => {
        if (!a.email) return false;
        const agencyEmail = a.email.toLowerCase();
        const agencyDomain = agencyEmail.split('@')[1];
        return agencyDomain === domain;
      });
      agency = matchingAgency || null;
    }
    
    setDetectedAgencyForReg(agency);
    setAgencyRegData({ 
      ...agencyRegData, 
      email,
      name: agency ? agency.name : "",
      phone: agency ? agency.phone : "",
      address: agency ? agency.address : ""
    });
    
    // If agency detected, show confirmation popup
    if (agency && email.includes('@')) {
      setPendingAgencyEmail(email);
      setShowAgencyConfirmation(true);
    }
  };
  
  const handleAgencyConfirmationLeave = () => {
    // Clear email and close all popups - user can use this email later
    setShowAgencyConfirmation(false);
    setShowVerificationCodePopup(false);
    setShowPersonalDetailsPopup(false);
    setAgencyVerificationCode("");
    setDetectedAgencyForReg(null);
    setPendingAgencyEmail("");
    setQuickRegisterData({ full_name: "", phone: "", password: "" });
    setAgencyRegData({
      name: "",
      contact_person: "",
      email: "",
      phone: "",
      address: "",
      password: ""
    });
  };
  
  const handleAgencyConfirmationConfirm = async () => {
    // User confirmed agency - send verification code to email
    setShowAgencyConfirmation(false);
    setLoading(true);
    
    try {
      // Send verification code using the correct endpoint
      await axios.post(`${API}/send-verification-code`, {
        email: pendingAgencyEmail
      });
      
      // Show verification code popup
      setAgencyVerificationCode("");
      setShowVerificationCodePopup(true);
      toast.success("Verification code sent to your email!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
      // Reset state so user can try again
      setDetectedAgencyForReg(null);
      setPendingAgencyEmail("");
    }
    setLoading(false);
  };
  
  const handleVerifyAgencyCode = async () => {
    if (!agencyVerificationCode || agencyVerificationCode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }
    
    setLoading(true);
    try {
      // Verify the code
      const response = await axios.post(`${API}/verify-code`, {
        email: pendingAgencyEmail,
        code: agencyVerificationCode
      });
      
      if (response.data.is_valid) {
        // Code verified - show personal details popup
        setShowVerificationCodePopup(false);
        setShowPersonalDetailsPopup(true);
        setQuickRegisterData({ full_name: "", phone: "", password: "" });
        toast.success("Email verified! Please enter your details.");
      } else {
        toast.error(response.data.message || "Invalid verification code");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify code");
    }
    setLoading(false);
  };
  
  const handleResendAgencyCode = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/send-verification-code`, {
        email: pendingAgencyEmail
      });
      setAgencyVerificationCode("");
      toast.success("New verification code sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    }
    setLoading(false);
  };
  
  const handleQuickRegister = async (e) => {
    e.preventDefault();
    
    if (!quickRegisterData.full_name || !quickRegisterData.password) {
      toast.error("Please fill in Full Name and Password");
      return;
    }
    
    setLoading(true);
    try {
      // Register as user under the detected agency (code was already verified)
      const registerPayload = {
        email: pendingAgencyEmail,
        password: quickRegisterData.password,
        full_name: quickRegisterData.full_name,
        phone: quickRegisterData.phone || "",
        agency_name: detectedAgencyForReg.name,
        agency_id: detectedAgencyForReg.id,
        role: "operations"
      };
      
      await axios.post(`${API}/auth/register`, registerPayload);
      toast.success("Registration submitted! Awaiting admin approval. You will be notified via email once approved.");
      
      // Reset everything
      setShowPersonalDetailsPopup(false);
      setShowVerificationCodePopup(false);
      setAgencyVerificationCode("");
      setDetectedAgencyForReg(null);
      setPendingAgencyEmail("");
      setQuickRegisterData({ full_name: "", phone: "", password: "" });
      setAgencyRegData({
        name: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        password: ""
      });
      setActiveTab("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      const { user, access_token } = response.data;
      
      // Set default full permissions for now (permissions endpoint removed)
      localStorage.setItem('userPermissions', JSON.stringify({
        flights_view: true, flights_edit: true, flights_save: true, flights_create: true,
        reports_view: true, reports_edit: true, reports_save: true, reports_create: true,
        agencies_view: true, agencies_edit: true, agencies_save: true, agencies_create: true
      }));
      
      toast.success("Welcome back!");
      onLogin(user, access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate against public email domains
    const publicDomains = [
      'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.fr',
      'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'live.com', 'live.co.uk',
      'outlook.com', 'outlook.co.uk', 'msn.com', 'icloud.com', 'me.com',
      'aol.com', 'mail.com', 'protonmail.com', 'proton.me', 'zoho.com',
      'yandex.com', 'mail.ru', 'gmx.com', 'gmx.net', 'inbox.com'
    ];
    
    const emailDomain = registerData.email.split('@')[1]?.toLowerCase();
    if (publicDomains.includes(emailDomain)) {
      toast.error("No public domain can be accepted. Use a company domain email address (not Gmail, Hotmail, Outlook, Yahoo, etc.)");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, registerData);
      toast.success("Registration submitted! Awaiting admin approval. You will be notified via email once approved.");
      setRegisterData({
        email: "",
        password: "",
        full_name: "",
        phone: "",
        address: "",
        agency_name: "",
        role: "operations"
      });
      setDetectedAgency(null);
      setActiveTab("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  const handleSendVerificationCode = async () => {
    if (!agencyRegData.email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/send-verification-code`, {
        email: agencyRegData.email
      });
      
      setCodeSent(true);
      toast.success("Verification code sent to your email! Please check your inbox.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification code");
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/verify-code`, {
        email: agencyRegData.email,
        code: verificationCode
      });
      
      if (response.data.is_valid) {
        setCodeVerified(true);
        toast.success("Email verified! You can now complete your registration.");
        // Trigger agency detection for the verified email
        handleAgencyEmailChange(agencyRegData.email);
      } else {
        toast.error(response.data.message || "Invalid verification code");
      }
    } catch (error) {
      toast.error("Failed to verify code");
    }
    setLoading(false);
  };

  const handleAgencyRegister = async (e) => {
    e.preventDefault();
    
    // Ensure email is verified before registration
    if (!codeVerified) {
      toast.error("Please verify your email first before registering");
      return;
    }
    
    // Validate against public email domains
    const publicDomains = [
      'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.fr',
      'hotmail.com', 'hotmail.co.uk', 'hotmail.fr', 'live.com', 'live.co.uk',
      'outlook.com', 'outlook.co.uk', 'msn.com', 'icloud.com', 'me.com',
      'aol.com', 'mail.com', 'protonmail.com', 'proton.me', 'zoho.com',
      'yandex.com', 'mail.ru', 'gmx.com', 'gmx.net', 'inbox.com'
    ];
    
    const emailDomain = agencyRegData.email.split('@')[1]?.toLowerCase();
    if (publicDomains.includes(emailDomain)) {
      toast.error("No public domain can be accepted. Use a company domain email address (not Gmail, Hotmail, Outlook, Yahoo, etc.)");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/agencies/register`, agencyRegData);
      toast.success("Registration submitted! Awaiting admin approval. You will be notified via email once approved.");
      // Reset form and verification states
      setAgencyRegData({
        name: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
        password: ""
      });
      setDetectedAgencyForReg(null);
      setCodeSent(false);
      setCodeVerified(false);
      setVerificationCode("");
      setActiveTab("login");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Registration failed";
      toast.error(errorMsg);
      console.error("Registration error:", errorMsg);
    }
    setLoading(false);
  };

  // "I Have a Code" Handler
  const handleVerifyExistingCode = async (e) => {
    e.preventDefault();
    if (!haveCodeData.email.trim() || !haveCodeData.code.trim()) {
      toast.error("Please enter both email and verification code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/verify-code`, {
        email: haveCodeData.email,
        code: haveCodeData.code
      });
      
      // Backend returns is_valid (not verified!)
      if (response.data.is_valid) {
        toast.success("✅ Code verified! Complete your registration below.");
        // Set the verified email and code in agency registration
        setAgencyRegData(prev => ({ ...prev, email: haveCodeData.email }));
        setVerificationCode(haveCodeData.code);
        setCodeVerified(true);
        setCodeSent(true);
        setActiveTab("agency");
        // Clear the have code form
        setHaveCodeData({ email: "", code: "" });
      } else {
        // Show the error message from backend
        toast.error(response.data.message || "Code verification failed. Please try again.");
      }
    } catch (error) {
      console.error("Code verification error:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.detail || "Invalid or expired code";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Handlers
  const handleRequestResetCode = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: resetEmail
      });
      
      toast.success("If this email is registered, you will receive a reset code shortly.");
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    }
    setResetLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!resetCode.trim()) {
      toast.error("Please enter the verification code");
      return;
    }
    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm your new password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    // Check for digit
    if (!/\d/.test(newPassword)) {
      toast.error("Password must contain at least one digit (0-9)");
      return;
    }
    
    // Check for special character
    const specialChars = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/]/;
    if (!specialChars.test(newPassword)) {
      toast.error("Password must contain at least one special character (!@#$%^&* etc.)");
      return;
    }

    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      
      toast.success("Password reset successfully! You can now login with your new password.");
      // Close dialog and reset states
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setResetEmail("");
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
    setResetLoading(false);
  };

  const handleCloseForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setResetEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0A1128' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-32 h-32 rounded-full mb-4 p-4" style={{ background: 'white' }}>
            <img 
              src={logoUrl} 
              alt="Air Transport Trade Logo" 
              className="w-full h-full object-contain"
              onError={(e) => { e.target.src = "https://customer-assets.emergentagent.com/job_rolebased-invoice/artifacts/q1mu9arn_chat_edit_image_20251029_170030.png"; }}
            />
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF', letterSpacing: '1px' }}>AIR TRANSPORT TRADE</h1>
          <p className="text-sm" style={{ color: '#B0B0B0' }}>VIP Service Management System</p>
        </div>

        <Card className="glass border max-h-[90vh] overflow-y-auto" style={{ borderColor: 'rgba(74, 90, 138, 0.4)', background: 'rgba(30, 42, 74, 0.6)', backdropFilter: 'blur(12px)' }} data-testid="login-card">
          <CardHeader>
            <CardTitle style={{ color: '#FFFFFF' }}>
              {activeTab === "login" ? "Sign In" : activeTab === "register" ? "Create Account" : "Agency Registration"}
            </CardTitle>
            <CardDescription style={{ color: '#B0B0B0' }}>
              {activeTab === "login" ? "Access your account" : activeTab === "register" ? "Register a new staff account" : "Register your travel agency"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab === "register" ? "register" : (activeTab === "agency" ? "agency" : (activeTab === "havecode" ? "havecode" : "login"))} onValueChange={handleTabChange}>
              {activeTab !== "register" && (
                // Login, Agency Registration, and I Have Code View
                <TabsList className="grid w-full grid-cols-3 mb-4" style={{ background: '#1E2A4A' }}>
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="agency" data-testid="agency-tab">Register</TabsTrigger>
                  <TabsTrigger value="havecode" data-testid="havecode-tab">I Have Code</TabsTrigger>
                </TabsList>
              )}
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" style={{ color: '#E8E8E8' }}>Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" style={{ color: '#E8E8E8' }}>Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                    <div className="text-right mt-1">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-xs hover:underline"
                        style={{ color: '#4A9FFF' }}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="login-submit"
                    style={{ background: '#FFFFFF', color: '#0A1128', fontWeight: '600' }}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <div className="mb-4">
                  <Button 
                    variant="ghost" 
                    onClick={() => setActiveTab("login")}
                    style={{ color: '#B0B0B0' }}
                  >
                    ← Back to Login
                  </Button>
                </div>
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* 1. Email - First */}
                  <div>
                    <Label htmlFor="register-email" style={{ color: '#E8E8E8' }}>Email *</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email"
                      type="email"
                      placeholder="your@company.com"
                      value={registerData.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                    {detectedAgency && (
                      <div className="mt-2 p-2 rounded text-sm" style={{ background: 'rgba(74, 222, 128, 0.1)', color: '#4ADE80', border: '1px solid rgba(74, 222, 128, 0.3)' }}>
                        ✓ Agency detected: <strong>{detectedAgency.name}</strong>
                      </div>
                    )}
                    {registerData.email && !detectedAgency && registerData.email.includes('@') && (
                      <div className="mt-2 p-2 rounded text-sm" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        ⚠ No agency found with this email domain.
                      </div>
                    )}
                  </div>

                  {/* 2. Agency Name - Auto-filled or Manual */}
                  <div>
                    <Label htmlFor="register-agency" style={{ color: '#E8E8E8' }}>
                      Agency Name {!detectedAgency && '*'}
                    </Label>
                    <Input
                      id="register-agency"
                      type="text"
                      placeholder={detectedAgency ? "Auto-detected" : "Enter agency name"}
                      value={registerData.agency_name}
                      onChange={(e) => setRegisterData({ ...registerData, agency_name: e.target.value })}
                      required={!detectedAgency}
                      disabled={!!detectedAgency}
                      style={{ 
                        background: detectedAgency ? '#2A3A5A' : '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: detectedAgency ? '#4ADE80' : '#E8E8E8',
                        cursor: detectedAgency ? 'not-allowed' : 'text'
                      }}
                    />
                    {!detectedAgency && registerData.email && (
                      <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                        No agency detected. Please enter agency name manually.
                      </p>
                    )}
                  </div>

                  {/* 3. Full Name */}
                  <div>
                    <Label htmlFor="register-name" style={{ color: '#E8E8E8' }}>Full Name *</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  {/* 4. Phone */}
                  <div>
                    <Label htmlFor="register-phone" style={{ color: '#E8E8E8' }}>Phone</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="+32 123 456 789"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  {/* 5. Address */}
                  <div>
                    <Label htmlFor="register-address" style={{ color: '#E8E8E8' }}>Address</Label>
                    <Input
                      id="register-address"
                      type="text"
                      placeholder="Street, City, Country"
                      value={registerData.address}
                      onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  {/* 6. Password - Last */}
                  <div>
                    <Label htmlFor="register-password" style={{ color: '#E8E8E8' }}>Password *</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  <div className="text-xs p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                    ℹ️ Your registration will be reviewed by admin. {detectedAgency ? 'You will be linked to your agency automatically.' : 'You will be registered as staff.'}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="register-submit"
                    style={{ background: '#FFFFFF', color: '#0A1128', fontWeight: '600' }}
                  >
                    {loading ? "Submitting..." : "Register Staff Account"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="agency">
                <form onSubmit={handleAgencyRegister} className="space-y-4">
                  {/* Step 1: Email Entry */}
                  <div className="p-4 rounded-lg" style={{ background: 'rgba(74, 165, 250, 0.1)', border: '1px solid rgba(74, 165, 250, 0.3)' }}>
                    <Label htmlFor="registration-email" style={{ color: '#E8E8E8', fontWeight: 'bold' }}>
                      Email Address *
                    </Label>
                    <p className="text-xs mb-2" style={{ color: '#B0B0B0' }}>
                      Enter your email to receive a verification code (valid for 5 minutes)
                    </p>
                    <Input
                      id="registration-email"
                      type="email"
                      placeholder="your@email.com"
                      value={agencyRegData.email}
                      onChange={(e) => handleAgencyEmailChange(e.target.value)}
                      disabled={codeSent || showVerificationCodePopup || showPersonalDetailsPopup}
                      required
                      style={{ 
                        background: '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: '#E8E8E8',
                        marginBottom: '8px'
                      }}
                    />
                    {!codeSent && !showVerificationCodePopup && !showPersonalDetailsPopup && !detectedAgencyForReg && (
                      <Button 
                        type="button"
                        onClick={handleSendVerificationCode}
                        disabled={loading || !agencyRegData.email}
                        className="w-full"
                        style={{ background: '#4A9FFF', color: '#FFF' }}
                      >
                        {loading ? 'Sending...' : 'Send Verification Code'}
                      </Button>
                    )}
                    {detectedAgencyForReg && !showVerificationCodePopup && !showPersonalDetailsPopup && !codeSent && (
                      <p className="text-sm mt-2" style={{ color: '#4ADE80' }}>
                        ✓ Known agency domain detected: <strong>{detectedAgencyForReg.name}</strong>
                        <br />
                        <span className="text-xs" style={{ color: '#B0B0B0' }}>A confirmation dialog will appear...</span>
                      </p>
                    )}
                  </div>

                  {/* Step 2: Code Verification (Only after code sent) */}
                  {codeSent && !codeVerified && (
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                      <Label htmlFor="verification-code" style={{ color: '#E8E8E8', fontWeight: 'bold' }}>
                        Verification Code * (6 digits)
                      </Label>
                      <p className="text-xs mb-2" style={{ color: '#FFD700' }}>
                        ⏰ Check your email inbox for the verification code (expires in 5 minutes)
                      </p>
                      <div className="flex gap-2">
                        <Input
                          id="verification-code"
                          type="text"
                          placeholder="000000"
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setVerificationCode(value);
                          }}
                          maxLength={6}
                          required
                          style={{ 
                            background: '#1E2A4A', 
                            borderColor: '#4A5A8A', 
                            color: '#E8E8E8',
                            fontSize: '18px',
                            letterSpacing: '4px',
                            textAlign: 'center'
                          }}
                        />
                        <Button 
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={loading || verificationCode.length !== 6}
                          style={{ background: '#4A9FFF', color: '#FFF', minWidth: '120px' }}
                        >
                          Verify Code
                        </Button>
                      </div>
                      <Button 
                        type="button"
                        onClick={() => {
                          setCodeSent(false);
                          setVerificationCode("");
                        }}
                        variant="ghost"
                        className="w-full mt-2 text-xs"
                        style={{ color: '#B0B0B0' }}
                      >
                        Didn't receive code? Send again
                      </Button>
                    </div>
                  )}

                  {/* Step 3: Success - Code Verified */}
                  {codeVerified && (
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)' }}>
                      <p style={{ color: '#4ADE80', fontWeight: 'bold' }}>✓ Email Verified!</p>
                      <p className="text-xs" style={{ color: '#B0B0B0' }}>
                        {agencyRegData.email} - Complete registration within 5 minutes or you'll need to verify again
                      </p>
                    </div>
                  )}

                  {/* Step 4: Registration Form (Only Visible After Code Verification) */}
                  {codeVerified && (
                    <>
                      {/* 1. Contact Person - User Name Only */}
                      <div>
                    <Label htmlFor="agency-contact" style={{ color: '#E8E8E8' }}>Contact Person (Your Name) *</Label>
                    <Input
                      id="agency-contact"
                      data-testid="agency-reg-contact"
                      type="text"
                      placeholder="John Doe"
                      value={agencyRegData.contact_person}
                      onChange={(e) => setAgencyRegData({ ...agencyRegData, contact_person: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                      Your name as the contact person for this account
                    </p>
                  </div>

                  {/* 3. Company Name - Auto-filled or Manual */}
                  <div>
                    <Label htmlFor="agency-name" style={{ color: '#E8E8E8' }}>Company Name *</Label>
                    <Input
                      id="agency-name"
                      data-testid="agency-reg-name"
                      type="text"
                      placeholder={detectedAgencyForReg ? "Auto-filled from existing agency" : "Brussels Travel Agency"}
                      value={agencyRegData.name}
                      onChange={(e) => setAgencyRegData({ ...agencyRegData, name: e.target.value })}
                      required
                      disabled={!!detectedAgencyForReg}
                      style={{ 
                        background: detectedAgencyForReg ? '#2A3A5A' : '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: detectedAgencyForReg ? '#4ADE80' : '#E8E8E8',
                        cursor: detectedAgencyForReg ? 'not-allowed' : 'text'
                      }}
                    />
                    {detectedAgencyForReg && (
                      <p className="text-xs mt-1" style={{ color: '#4ADE80' }}>
                        Auto-filled from agency database (existing agency)
                      </p>
                    )}
                  </div>

                  {/* 4. Phone - Auto-filled when agency detected */}
                  <div>
                    <Label htmlFor="agency-phone" style={{ color: '#E8E8E8' }}>Phone *</Label>
                    <Input
                      id="agency-phone"
                      data-testid="agency-reg-phone"
                      type="tel"
                      placeholder="+32 2 123 4567"
                      value={agencyRegData.phone}
                      onChange={(e) => setAgencyRegData({ ...agencyRegData, phone: e.target.value })}
                      required
                      disabled={!!detectedAgencyForReg}
                      style={{ 
                        background: detectedAgencyForReg ? '#2A3A5A' : '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: detectedAgencyForReg ? '#4ADE80' : '#E8E8E8',
                        cursor: detectedAgencyForReg ? 'not-allowed' : 'text'
                      }}
                    />
                    {detectedAgencyForReg && (
                      <p className="text-xs mt-1" style={{ color: '#4ADE80' }}>
                        Auto-filled from agency database
                      </p>
                    )}
                  </div>

                  {/* 5. Address - Auto-filled when agency detected */}
                  <div>
                    <Label htmlFor="agency-address" style={{ color: '#E8E8E8' }}>Address *</Label>
                    <Input
                      id="agency-address"
                      data-testid="agency-reg-address"
                      type="text"
                      placeholder="Avenue Louise 123, 1050 Brussels"
                      value={agencyRegData.address}
                      onChange={(e) => setAgencyRegData({ ...agencyRegData, address: e.target.value })}
                      required
                      disabled={!!detectedAgencyForReg}
                      style={{ 
                        background: detectedAgencyForReg ? '#2A3A5A' : '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: detectedAgencyForReg ? '#4ADE80' : '#E8E8E8',
                        cursor: detectedAgencyForReg ? 'not-allowed' : 'text'
                      }}
                    />
                    {detectedAgencyForReg && (
                      <p className="text-xs mt-1" style={{ color: '#4ADE80' }}>
                        Auto-filled from agency database
                      </p>
                    )}
                  </div>

                  {/* 6. Password - Last */}
                  <div>
                    <Label htmlFor="agency-password" style={{ color: '#E8E8E8' }}>Password *</Label>
                    <Input
                      id="agency-password"
                      data-testid="agency-reg-password"
                      type="password"
                      placeholder="••••••••"
                      value={agencyRegData.password}
                      onChange={(e) => setAgencyRegData({ ...agencyRegData, password: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  <div className="text-xs p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                    {detectedAgencyForReg ? (
                      <>
                        ℹ️ <strong>Existing Agency Detected:</strong> Company info auto-filled. Contact person is for your user account only and won't change agency information.
                      </>
                    ) : (
                      <>
                        ℹ️ <strong>New Agency Registration:</strong> Your registration will create a new agency and will be reviewed by admin.
                      </>
                    )}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="agency-reg-submit"
                    style={{ background: '#FFFFFF', color: '#0A1128', fontWeight: '600' }}
                  >
                    {loading ? "Submitting..." : "Register Agency"}
                  </Button>
                  </>
                  )}
                </form>
              </TabsContent>

              {/* I Have a Code Tab */}
              <TabsContent value="havecode">
                <form onSubmit={handleVerifyExistingCode} className="space-y-4">
                  <div className="p-4 rounded-lg mb-4" style={{ background: 'rgba(74, 159, 255, 0.1)', border: '1px solid rgba(74, 159, 255, 0.3)' }}>
                    <p className="text-sm" style={{ color: '#4A9FFF', fontWeight: 'bold' }}>Already have a verification code?</p>
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                      If you refreshed the page during registration, enter your email and verification code here to continue.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="havecode-email" style={{ color: '#E8E8E8' }}>Email Address</Label>
                    <Input
                      id="havecode-email"
                      type="email"
                      placeholder="your@email.com"
                      value={haveCodeData.email}
                      onChange={(e) => setHaveCodeData({ ...haveCodeData, email: e.target.value })}
                      required
                      style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                    />
                  </div>

                  <div>
                    <Label htmlFor="havecode-code" style={{ color: '#E8E8E8' }}>6-Digit Verification Code</Label>
                    <Input
                      id="havecode-code"
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      value={haveCodeData.code}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setHaveCodeData({ ...haveCodeData, code: value });
                      }}
                      required
                      style={{ 
                        background: '#1E2A4A', 
                        borderColor: '#4A5A8A', 
                        color: '#E8E8E8',
                        fontSize: '24px',
                        letterSpacing: '8px',
                        textAlign: 'center'
                      }}
                    />
                    <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                      Enter the 6-digit code that was sent to your email
                    </p>
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading || haveCodeData.code.length !== 6}
                    className="w-full"
                    style={{ background: '#4A9FFF', color: '#FFF' }}
                  >
                    {loading ? "Verifying..." : "Verify & Continue"}
                  </Button>

                  <div className="text-center text-xs" style={{ color: '#B0B0B0' }}>
                    <p>Code expires after 5 minutes</p>
                    <p className="mt-2">
                      Don't have a code?{' '}
                      <button 
                        type="button"
                        onClick={() => setActiveTab("agency")}
                        className="underline"
                        style={{ color: '#4A9FFF' }}
                      >
                        Start registration
                      </button>
                    </p>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: '#4A5A8A', backdropFilter: 'blur(12px)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Reset Password</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {forgotPasswordStep === 1 
                ? "Enter your email to receive a verification code"
                : "Enter the code and create a new password"
              }
            </DialogDescription>
          </DialogHeader>

          {forgotPasswordStep === 1 ? (
            // Step 1: Request Reset Code
            <form onSubmit={handleRequestResetCode} className="space-y-4">
              <div>
                <Label htmlFor="reset-email" style={{ color: '#E8E8E8' }}>Email Address</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="your@email.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  We'll send a 6-digit verification code to this email
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseForgotPassword}
                  className="flex-1"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1"
                  style={{ background: '#FFD700', color: '#0A1128', fontWeight: '600' }}
                >
                  {resetLoading ? "Sending..." : "Send Code"}
                </Button>
              </div>
            </form>
          ) : (
            // Step 2: Verify Code and Reset Password
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset-code" style={{ color: '#E8E8E8' }}>Verification Code</Label>
                <Input
                  id="reset-code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  maxLength={6}
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Check your email: {resetEmail}
                </p>
              </div>
              <div>
                <Label htmlFor="new-password" style={{ color: '#E8E8E8' }}>New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
                <div className="text-xs mt-1 space-y-1" style={{ color: '#B0B0B0' }}>
                  <p>Password must contain:</p>
                  <ul className="list-disc ml-4">
                    <li>At least 6 characters</li>
                    <li>At least 1 digit (0-9)</li>
                    <li>At least 1 special character (!@#$%^&* etc.)</li>
                  </ul>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm-password" style={{ color: '#E8E8E8' }}>Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForgotPasswordStep(1)}
                  className="flex-1"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="flex-1"
                  style={{ background: '#FFD700', color: '#0A1128', fontWeight: '600' }}
                >
                  {resetLoading ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Agency Confirmation Dialog - shown when known domain detected */}
      <Dialog open={showAgencyConfirmation} onOpenChange={setShowAgencyConfirmation}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: '#4A5A8A', backdropFilter: 'blur(12px)', maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Confirm Agency Registration</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              You are about to register for this agency
            </DialogDescription>
          </DialogHeader>

          {detectedAgencyForReg && (
            <div className="space-y-4">
              {/* Agency Name Highlight */}
              <div className="p-4 rounded-lg text-center" style={{ background: 'rgba(74, 165, 250, 0.2)', border: '1px solid rgba(74, 165, 250, 0.4)' }}>
                <h3 className="text-xl font-bold" style={{ color: '#FFFFFF' }}>
                  {detectedAgencyForReg.name}
                </h3>
              </div>

              {/* Agency Details */}
              <div className="space-y-3 p-4 rounded-lg" style={{ background: '#1E2A4A' }}>
                <div className="flex items-start gap-2">
                  <span style={{ color: '#B0B0B0', minWidth: '80px' }}>📍 Address:</span>
                  <span style={{ color: '#FFFFFF' }}>{detectedAgencyForReg.address || 'Not provided'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span style={{ color: '#B0B0B0', minWidth: '80px' }}>📞 Phone:</span>
                  <span style={{ color: '#FFFFFF' }}>{detectedAgencyForReg.phone || 'Not provided'}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span style={{ color: '#B0B0B0', minWidth: '80px' }}>🏢 VAT:</span>
                  <span style={{ color: '#FFFFFF' }}>{detectedAgencyForReg.vat_number || 'Not provided'}</span>
                </div>
              </div>

              <p className="text-sm text-center" style={{ color: '#FFD700' }}>
                ℹ️ Is this your agency? Click Confirm to continue registration.
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAgencyConfirmationLeave}
                  className="flex-1"
                  style={{ borderColor: '#EF4444', color: '#EF4444' }}
                >
                  Leave
                </Button>
                <Button
                  type="button"
                  onClick={handleAgencyConfirmationConfirm}
                  className="flex-1"
                  style={{ background: '#10B981', color: '#FFFFFF', fontWeight: '600' }}
                >
                  Confirm
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verification Code Popup - Step 2: Enter verification code */}
      <Dialog open={showVerificationCodePopup} onOpenChange={(open) => !open && handleAgencyConfirmationLeave()}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: '#4A5A8A', backdropFilter: 'blur(12px)', maxWidth: '400px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Enter Verification Code</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              A 6-digit code was sent to your email
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Agency Name Banner */}
            {detectedAgencyForReg && (
              <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)' }}>
                <span style={{ color: '#10B981', fontWeight: '600' }}>
                  🏢 {detectedAgencyForReg.name}
                </span>
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <Label style={{ color: '#E8E8E8' }}>Email</Label>
              <Input
                type="email"
                value={pendingAgencyEmail}
                disabled
                style={{ background: '#2A3A5A', borderColor: '#4A5A8A', color: '#4ADE80', cursor: 'not-allowed' }}
              />
            </div>

            {/* Verification Code Input */}
            <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
              <Label style={{ color: '#FFD700', fontWeight: 'bold' }}>Verification Code * (6 digits)</Label>
              <p className="text-xs mb-3" style={{ color: '#B0B0B0' }}>
                ⏰ Code expires in 5 minutes. Check your inbox and spam folder.
              </p>
              <Input
                type="text"
                placeholder="000000"
                value={agencyVerificationCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setAgencyVerificationCode(val);
                }}
                maxLength={6}
                style={{ background: '#1E2A4A', borderColor: '#FFD700', color: '#FFD700', letterSpacing: '8px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold' }}
              />
              <Button
                type="button"
                variant="ghost"
                onClick={handleResendAgencyCode}
                disabled={loading}
                className="w-full mt-3 text-xs"
                style={{ color: '#B0B0B0' }}
              >
                Didn't receive code? Send again
              </Button>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleAgencyConfirmationLeave}
                className="flex-1"
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleVerifyAgencyCode}
                disabled={loading || agencyVerificationCode.length !== 6}
                className="flex-1"
                style={{ background: '#4A9FFF', color: '#FFFFFF', fontWeight: '600' }}
              >
                {loading ? "Verifying..." : "Verify Code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal Details Popup - Step 3: Enter personal details */}
      <Dialog open={showPersonalDetailsPopup} onOpenChange={(open) => !open && handleAgencyConfirmationLeave()}>
        <DialogContent style={{ background: 'rgba(30, 42, 74, 0.95)', borderColor: '#4A5A8A', backdropFilter: 'blur(12px)', maxWidth: '450px' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Complete Registration</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Enter your personal details to finish
            </DialogDescription>
          </DialogHeader>

          {detectedAgencyForReg && (
            <form onSubmit={handleQuickRegister} className="space-y-4">
              {/* Agency Name Banner */}
              <div className="p-3 rounded-lg text-center" style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)' }}>
                <span style={{ color: '#10B981', fontWeight: '600' }}>
                  🏢 {detectedAgencyForReg.name}
                </span>
              </div>

              {/* Email Verified Badge */}
              <div className="p-2 rounded-lg flex items-center gap-2" style={{ background: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)' }}>
                <span style={{ color: '#4ADE80' }}>✓</span>
                <span style={{ color: '#4ADE80', fontSize: '14px' }}>{pendingAgencyEmail}</span>
                <span className="text-xs" style={{ color: '#B0B0B0' }}>(verified)</span>
              </div>

              {/* Full Name */}
              <div>
                <Label style={{ color: '#E8E8E8' }}>Full Name *</Label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={quickRegisterData.full_name}
                  onChange={(e) => setQuickRegisterData({ ...quickRegisterData, full_name: e.target.value })}
                  required
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              {/* Phone Number */}
              <div>
                <Label style={{ color: '#E8E8E8' }}>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="+32 123 456 789"
                  value={quickRegisterData.phone}
                  onChange={(e) => setQuickRegisterData({ ...quickRegisterData, phone: e.target.value })}
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>

              {/* Password */}
              <div>
                <Label style={{ color: '#E8E8E8' }}>Password *</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={quickRegisterData.password}
                  onChange={(e) => setQuickRegisterData({ ...quickRegisterData, password: e.target.value })}
                  required
                  minLength={6}
                  style={{ background: '#1E2A4A', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>
                  Minimum 6 characters
                </p>
              </div>

              {/* Info */}
              <div className="text-xs p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', color: '#FFD700', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                ℹ️ Your registration will be submitted for admin approval. You will be notified via email once approved.
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAgencyConfirmationLeave}
                  className="flex-1"
                  style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                  style={{ background: '#FFFFFF', color: '#0A1128', fontWeight: '600' }}
                >
                  {loading ? "Registering..." : "Register"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
